import { ChevronDown, ChevronsUpDown, ChevronUp } from 'lucide-react';
import Alert from './Alert';
import EmptyState from './EmptyState';
import { TableSkeleton } from './Loader';

const alignment = {
  left: '!text-left',
  right: '!text-right',
  center: '!text-center',
};

const sortIcons = {
  asc: ChevronUp,
  desc: ChevronDown,
  none: ChevronsUpDown,
};

const sortIconClass = {
  asc: 'h-3.5 w-3.5 opacity-100',
  desc: 'h-3.5 w-3.5 opacity-100',
  none: 'h-3.5 w-3.5 opacity-40',
};

const ariaSort = {
  asc: 'ascending',
  desc: 'descending',
  none: 'none',
};

// Filter controls are block-level, so they ignore the cell's text-align and would sit at the
// column's leading edge however the column itself is aligned.
const filterAlignment = {
  left: 'flex justify-start',
  right: 'flex justify-end',
  center: 'flex justify-center',
};

const DataTable = ({
  columns = [],
  rows = [],
  rowKey,
  loading = false,
  error = null,
  invalidDateRange = false,
  empty,
  onRowClick,
  serial = false,
  serialStart = 0,
  sort = '',
  onSortChange,
}) => {
  const notice = error ? (
    <Alert variant="danger">{error}</Alert>
  ) : invalidDateRange ? (
    <EmptyState title="Invalid date range" message="The start date cannot be later than the end date." />
  ) : (
    <EmptyState title={empty?.title} message={empty?.message} />
  );

  // An interactive cell holds its own controls, so neither a click nor Enter in it may open the row.
  const stopRowActivation = (event) => event.stopPropagation();

  const handleKeyDown = (event, row) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    onRowClick(row);
  };

  const sortStateOf = (column) => {
    if (sort === column.sortKey) return 'asc';
    if (sort === `-${column.sortKey}`) return 'desc';
    return 'none';
  };

  // Two states, not three: the server always applies a default sort, so an "unsorted" state would
  // reorder the table into something no header could indicate.
  const toggleSort = (column) => {
    const state = sortStateOf(column);
    const nextDesc = state === 'none' ? column.sortDefault === 'desc' : state === 'asc';
    onSortChange(nextDesc ? `-${column.sortKey}` : column.sortKey);
  };

  const hasFilters = columns.some((column) => column.filter);
  const span = columns.length + (serial ? 1 : 0);

  return (
    <div className="table-responsive">
      <table className="table table-hover mb-0">
        <thead>
          <tr>
            {serial && (
              <th scope="col" className={`${alignment.right} w-16`}>
                S/N
              </th>
            )}
            {columns.map((column) => {
              const sortable = Boolean(column.sortKey && onSortChange);
              const state = sortable ? sortStateOf(column) : null;
              const SortIcon = sortable ? sortIcons[state] : null;

              return (
                <th
                  key={column.key}
                  scope="col"
                  aria-sort={sortable ? ariaSort[state] : undefined}
                  className={alignment[column.align] ?? alignment.left}
                >
                  {sortable ? (
                    <button
                      type="button"
                      onClick={() => toggleSort(column)}
                      className="inline-flex items-center gap-1"
                    >
                      {column.header}
                      <SortIcon className={sortIconClass[state]} aria-hidden="true" />
                    </button>
                  ) : (
                    column.header
                  )}
                </th>
              );
            })}
          </tr>
          {hasFilters && (
            <tr className="[&>th]:!py-2 [&>th]:!font-normal [&>th]:border-t-0">
              {serial && <th aria-hidden="true" />}
              {columns.map((column) => (
                <th key={column.key}>
                  <div className={filterAlignment[column.align] ?? filterAlignment.left}>
                    {column.filter ?? null}
                  </div>
                </th>
              ))}
            </tr>
          )}
        </thead>
        {loading ? (
          <TableSkeleton rows={6} cols={span} />
        ) : (
          <tbody>
            {error || invalidDateRange || rows.length === 0 ? (
              <tr>
                <td colSpan={span} className="!p-4">
                  {notice}
                </td>
              </tr>
            ) : (
              rows.map((row, index) => (
                <tr
                  key={rowKey(row)}
                  className={onRowClick ? 'cursor-pointer' : undefined}
                  tabIndex={onRowClick ? 0 : undefined}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  onKeyDown={onRowClick ? (event) => handleKeyDown(event, row) : undefined}
                >
                  {serial && (
                    <td className={`${alignment.right} text-theme-secondarytextcolor tabular-nums`}>
                      {serialStart + index + 1}
                    </td>
                  )}
                  {columns.map((column) => (
                    <td
                      key={column.key}
                      className={`${alignment[column.align] ?? alignment.left} ${column.className ?? ''}`}
                      onClick={column.interactive ? stopRowActivation : undefined}
                      onKeyDown={column.interactive ? stopRowActivation : undefined}
                    >
                      {column.render ? column.render(row) : row[column.key]}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        )}
      </table>
    </div>
  );
};

export default DataTable;
